import React, { PureComponent, Fragment } from 'react';
import cn from 'classnames';
import numeral from 'numeral';
import moment from 'moment';
import { orderBy } from 'lodash';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { array, object, number, bool } from 'prop-types';
import StatisticsTableRow from './StatisticsTableRow';
import StatisticsTableGroup from './StatisticsTableGroup';
import { StatisticsTableField } from './index';
import {
  getLinkToConversationTable,
  getUtlParams,
  removeEmptyStats,
  isMaster,
} from './statisticsHelpers';
import StickyTable from '../../hoc/StickyTable';
import WithHover from '../../hoc/WithHover';
import {
  getZoneOffsetByName,
  _toFixed,
  isString,
  getToFixedParams,
  isWeekend,
  getStringOrSingleArrayValue,
  LocalStorage,
  currencyToSign,
} from '../../utils/utils';
import { FMessage } from '../../hoc/IntlComponents';
import DeleteMemberIconBtn from '../MasterAccount/DeleteMemberIconBtn';
import Icon from '../Icon/Icon';
import { FormattedMessage } from 'react-intl';
import EditingModal from '../../modals/EditingModal/EditingModal';
import ActionModal from '../../modals/ActionModal/ActionModal';
import AddMemberModal from '../../modals/AddMemberModal/AddMemberModal';

const HIDDEN_COLUMNS_STORAGE_KEY = 'statistics_table_hidden_columns';
const SORT_STORAGE_KEY = 'statistics_table_sort';

class StatisticsTable extends PureComponent {
  static propTypes = {
    stats: array.isRequired,
    total: object.isRequired,
    config: array.isRequired,
    countries: array.isRequired,
    sites: array.isRequired,
    currency: number.isRequired,
    dict: object,
    flows: array.isRequired,
    offers: array.isRequired,
    isLoadingScreenshot: bool,
    theme: number.isRequired,
  };

  static defaultProps = {
    stats: [],
    total: {},
    config: [],
    flows: [],
    offers: [],
    sites: [],
    countries: [],
    currency: 0,
    theme: 1,
  };

  constructor(props) {
    super(props);
    const { stats, currency, tab, config } = props;

    const savedHiddenColumns = this.getSavedHiddenColumns(tab);
    const savedSort = this.getSavedSort(tab);

    this.state = {
      sort: savedSort || this.getStartSort(tab),
      stats: stats,
      config,
      hiddenColumns: savedHiddenColumns,
      pendingHiddenColumns: savedHiddenColumns,
      isAddMemberModalOpen: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { stats, currency, config, tab, hasTableEditingModalOpen, loading } = this.props;

    if (stats !== prevProps.stats || currency !== prevProps.currency) {
      if (!stats.length) {
        this.props.setPagesCount();
      }

      this.setState({
        stats: stats,
        config: this.mergeConfigWithStorage(config),
      });
    }

    if (tab !== prevProps.tab) {
      const savedHiddenColumns = this.getSavedHiddenColumns(tab);
      const savedSort = this.getSavedSort(tab);

      this.setState({
        sort: savedSort || this.getStartSort(tab),
        hiddenColumns: savedHiddenColumns,
        pendingHiddenColumns: savedHiddenColumns,
      });
    }

    if (hasTableEditingModalOpen !== prevProps.hasTableEditingModalOpen) {
      if (hasTableEditingModalOpen) {
        this.setState({ pendingHiddenColumns: [...this.state.hiddenColumns] });
      } else {
        this.setState({ pendingHiddenColumns: [...this.state.hiddenColumns] });
      }
    }
  }

  getStorageKey = (tab = null, prefix = '') => {
    const currentTab = tab || this.props.tab;
    if (!currentTab) {
      return `${prefix}${HIDDEN_COLUMNS_STORAGE_KEY}`;
    }
    return `${prefix}${HIDDEN_COLUMNS_STORAGE_KEY}_${currentTab}`;
  };

  getSortStorageKey = (tab = null) => {
    const currentTab = tab || this.props.tab;
    if (!currentTab) {
      return SORT_STORAGE_KEY;
    }
    return `${SORT_STORAGE_KEY}_${currentTab}`;
  };

  getSavedHiddenColumns = (tab = null) => {
    try {
      const storageKey = this.getStorageKey(tab);
      const saved = LocalStorage.get(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading hidden columns from LocalStorage:', error);
      return [];
    }
  };

  getSavedSort = (tab = null) => {
    try {
      const storageKey = this.getSortStorageKey(tab);
      const saved = LocalStorage.get(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.field !== 'undefined' && typeof parsed.order !== 'undefined') {
          return parsed;
        }
      }
      return null;
    } catch (error) {
      console.error('Error loading sort from LocalStorage:', error);
      return null;
    }
  };

  saveHiddenColumnsToStorage = (hiddenColumns, tab = null) => {
    try {
      const storageKey = this.getStorageKey(tab);
      LocalStorage.set(storageKey, JSON.stringify(hiddenColumns));
    } catch (error) {
      console.error('Error saving hidden columns to LocalStorage:', error);
    }
  };

  saveSortToStorage = (sort, tab = null) => {
    try {
      const storageKey = this.getSortStorageKey(tab);
      LocalStorage.set(storageKey, JSON.stringify(sort));
    } catch (error) {
      console.error('Error saving sort to LocalStorage:', error);
    }
  };

  mergeConfigWithStorage = (config) => {
    return config;
  };

  getStartSort = (tab) => ({
    field: tab === 'times' ? 3 : 127,
    order: -1,
    inverse: false,
  });

  getAllGroups = () => {
    const { config, customStrings = {} } = this.props;
    const { group: customTitles } = customStrings;

    const groups = new Set();

    config.forEach((column) => {
      if (column.isRendering !== false) {
        const groupId = column.group || '__EMPTY__';
        let groupTitle = groupId;

        if (customTitles && customTitles[groupId]) {
          groupTitle = customTitles[groupId];
        }

        groups.add(groupTitle);
      }
    });

    return Array.from(groups);
  };

  togglePendingColumnVisibility = (columnId) => {
    this.setState((prevState) => {
      const pendingHiddenColumns = [...prevState.pendingHiddenColumns];
      const columnIndex = pendingHiddenColumns.indexOf(columnId);

      if (columnIndex > -1) {
        pendingHiddenColumns.splice(columnIndex, 1);
      } else {
        pendingHiddenColumns.push(columnId);
      }

      return { pendingHiddenColumns };
    });
  };

  applyColumnChanges = () => {
    const { pendingHiddenColumns } = this.state;
    const { tab, handleHasEditingModalToggle } = this.props;

    this.setState(
      {
        hiddenColumns: [...pendingHiddenColumns],
      },
      () => {
        this.saveHiddenColumnsToStorage(pendingHiddenColumns, tab);
        handleHasEditingModalToggle();
      },
    );
  };

  handleCancelChanges = () => {
    this.setState({ pendingHiddenColumns: [...this.state.hiddenColumns] });
    this.props.handleHasEditingModalToggle();
  };

  handleToggleAddMemberModal = () => {
    this.setState((prevState) => ({
      isAddMemberModalOpen: !prevState.isAddMemberModalOpen,
    }));
  };

  isColumnHidden = (columnId) => {
    return this.state.hiddenColumns.includes(columnId);
  };

  isPendingColumnHidden = (columnId) => {
    return this.state.pendingHiddenColumns.includes(columnId);
  };

  getFormattedValue = (value, type, statId, stat, isTotal) => {
    const {
      sites,
      countries,
      dict,
      filters,
      timeZones,
      offers,
      flows,
      tab,
      currency,
      slaves,
      isMembers,
      isMember,
    } = this.props;
    const { config } = this.state;
    const tz = filters.timeZone;
    let timeZone = '';
    if (tz) {
      timeZone = getZoneOffsetByName(timeZones, tz);
    }
    const { numFormat, strFormat } = getToFixedParams(config, statId);
    switch (type) {
      case 'int':
        const intValue = numeral(value).format('0,0');
        if ([127, 27, 28, 29, 30].find((id) => id === statId)) {
          return getLinkToConversationTable({
            value: intValue,
            statId,
            tab,
            stat,
            config,
            timeZones,
            isTotal,
            currency,
            isMembers,
          });
        }
        return intValue;
      case 'float':
        return <span>{numeral(_toFixed(value, numFormat)).format(strFormat)}</span>;
      case 'money':
        const currencyString = currency === 0 ? 'rub' : currency === 1 ? 'usd' : 'eur';
        return (
          <span className="sum">
            {value % 1 === 0 ? value : numeral(_toFixed(value, numFormat)).format(strFormat)}&nbsp;
            {currencyToSign(currencyString)}
          </span>
        );
      case 'string':
        return value.toString();
      case 'date':
        return (
          <span className="date">
            {moment.unix(value).utcOffset(timeZone).format('DD.MM.YYYY')}{' '}
            <span className="weekend">{isWeekend(value, timeZone)}</span>
          </span>
        );
      case 'datetime':
        if (value === 0) break;
        return moment.unix(value).utcOffset(timeZone).format('DD MMM YY LT');
      case 'hours':
        const from = value > 9 ? value : `0${value}`;
        let to = value + 1 === 24 ? 0 : value + 1;
        to = to > 9 ? to : `0${to}`;
        return (
          <span>
            {from}:00&nbsp;-&nbsp;{to}:00
          </span>
        );
      case 'stream_id':
        let stream =
          dict['flows'] !== undefined && dict['flows'].find((stream) => stream.id === value);
        if (!stream && flows) {
          stream = flows.find(({ id }) => id === value);
        }
        return stream && isString(stream.title) ? (
          !isMember ? (
            <Link to={`/tools/flows/${value}`} target="_blank">
              {stream.title}
            </Link>
          ) : (
            stream.title
          )
        ) : (
          value
        );
      case 'webmaster_id':
        let webmaster = slaves !== undefined && slaves.find((slave) => slave.id === value);
        return webmaster && isString(webmaster.title) ? (
          <Link to={`/tools/master/${value}?template=7`}>{webmaster.title}</Link>
        ) : (
          value
        );
      case 'site_id':
        let site =
          dict['sources'] !== undefined && dict['sources'].find((site) => site.id === value);
        if (!site && sites) {
          site = sites.find((site) => site.id === value);
        }
        return site && isString(site.title) ? site.title : value;
      case 'offer_id':
        let offer;

        if (dict['offers'] !== undefined) {
          offer = dict['offers'].find((offer) => offer.id === value);
        }
        if (!offer && offers) {
          offer = offers.find((offer) => offer.id === value);
        }

        return offer && isString(offer.title) ? (
          !isMember ? (
            <div className="cell-offers">
              <Link to={`/offers/${offer.id}`}>{offer.title}</Link>
              {offer.disabled && (
                <>
                  <Icon id={`blocked_offer_${offer.id}`} type="warning" />
                  <UncontrolledTooltip
                    placement="right-start"
                    trigger="legacy hover"
                    modifiers={{
                      offset: {
                        offset: '0, 5px',
                      },
                    }}
                    target={`blocked_offer_${offer.id}`}
                  >
                    <FormattedMessage
                      id="flows.table.offer-disabled"
                      defaultValue="Offer disabled! Please redirect your traffic to another offer!"
                    />
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          ) : (
            offer.title
          )
        ) : (
          value
        );
      case 'country_iso':
        const country = countries.find((country) => country.id === value);
        return country ? country.title : value;
      case 'ratio':
        return value;
      case 'brand_id':
        let device;

        if (dict['devices'] !== undefined) {
          device = dict['devices'].find((vendor) => vendor.id === value);
        }

        return device ? device.title : value;
      case 'browser_id':
        let browser;

        if (dict['browsers'] !== undefined) {
          browser = dict['browsers'].find((vendor) => vendor.id === value);
        }

        return browser ? browser.title : value;
      case 'os_id':
        let os;

        if (dict['os'] !== undefined) {
          os = dict['os'].find((vendor) => vendor.id === value);
        }

        return os ? os.title : value;
      case 'landing_id':
        let land;

        if (dict['landings'] !== undefined) {
          land = dict['landings'].find(({ id }) => id === value);
        }

        return land ? land.title : value;
      case 'transit_id':
        let transit;

        if (dict['transits'] !== undefined) {
          transit = dict['transits'].find(({ id }) => id === value);
        }

        return transit ? transit.title : value;
      case 'subacc':
        if (isTotal) {
          return this.renderSubacc(value);
        } else {
          const subAccLabel = !value ? '—' : value.label;
          return this.renderSubacc(subAccLabel);
        }
      default:
        return value;
    }
  };

  renderSubacc = (value) => {
    if (value === null || value === undefined) {
      return value;
    }
    return (
      <Fragment>
        <span id={value} title={value}>
          {value.length > 40 ? value.substr(0, 5) : value}
          {value.length > 40 ? '...' + value.substr(value.length - 5) : ''}
        </span>
      </Fragment>
    );
  };

  normalizeData = (stat, isTotal) => {
    const { currency } = this.props;
    const { config, hiddenColumns } = this.state;

    return config
      .filter((cfg) => !hiddenColumns.includes(cfg.id))
      .map(({ id, type, isRendering }) => {
        if (stat[id] && isRendering !== false) {
          return this.getFormattedValue(stat[id][currency], type, id, stat, isTotal);
        }
      });
  };

  makeFields = () => {
    const { currency } = this.props;
    const { config, hiddenColumns } = this.state;

    return config
      .filter((cfg) => !hiddenColumns.includes(cfg.id))
      .map((cfg) => ({
        id: cfg.id,
        title: cfg.title || '',
        is_sortable: cfg.sortable || false,
        type: cfg.type,
        inverse: cfg.type === 'ratio' || false,
        label: cfg.label || '',
        postfix: cfg.postfix[currency],
        isRendering: cfg.isRendering,
      }));
  };

  renderTableGroups = () => {
    const { isMembers, customStrings = {}, currency, currencies } = this.props;
    const { config, hiddenColumns } = this.state;
    const { group: customTitles } = customStrings;

    const groups = config
      .filter((item) => item.isRendering !== false && !hiddenColumns.includes(item.id))
      .reduce((fields, item, i) => {
        if (item.isRendering === false) return fields;

        const { group } = item;
        const key = group ? group : `__EMPTY__${i}`;

        return { ...fields, [key]: fields[group] ? fields[group] + 1 : 1 };
      }, {});

    const tableGroups = Object.keys(groups).map((title) => {
      let formattedTitle = title;
      if (customTitles && customTitles[title]) {
        formattedTitle = customTitles[title];
      }
      return (
        <StatisticsTableGroup
          key={title}
          colSpan={groups[title]}
          title={formattedTitle}
          currency={currencies[currency]}
        />
      );
    });
    if (!tableGroups.length) {
      return null;
    }
    if (isMembers) {
      tableGroups.push(<th key="delete_members" />);
    }
    return tableGroups;
  };

  handleSort = (field, inverse, e) => {
    e.preventDefault();

    const { sort } = this.state;
    let order = field === sort.field ? sort.order * -1 : 1;

    const newSort = {
      field,
      order,
      inverse,
    };

    this.setState({ sort: newSort }, () => {
      this.saveSortToStorage(newSort, this.props.tab);
    });
  };

  renderTableFields = () => {
    const fields = this.makeFields();
    const { sort } = this.state;
    const { isMembers, template } = this.props;

    const tableFields = fields.map((field) => (
      <StatisticsTableField
        onSort={this.handleSort}
        {...field}
        key={field.id}
        sorted={sort.field === field.id}
        order={sort.order}
        template={template}
      />
    ));
    if (!tableFields.length) {
      return null;
    }
    if (isMembers) {
      tableFields.push(<StatisticsTableField title="" key={fields.length} />);
    }
    return tableFields;
  };

  getOrderedStats = () => {
    const { sort, stats, config } = this.state;
    const { currency, dict, flows } = this.props;
    const direction = sort.inverse ? sort.order * -1 : sort.order * 1;

    if (stats.length > 0 && stats[0][sort.field] !== undefined) {
      let type = config.find((cnf) => cnf.id === sort.field);
      type = type !== undefined ? type.type : '';

      return orderBy(
        stats,
        (item) => {
          let value = item[sort.field][currency];

          switch (type) {
            case 'stream_id':
              let stream =
                dict['flows'] !== undefined && dict['flows'].find((stream) => stream.id === value);
              if (!stream && flows) {
                stream = flows.find(({ id }) => id === value);
              }
              return stream && isString(stream.title) ? stream.title.toLowerCase() : value;

            default:
              return value;
          }
        },
        direction > 0 ? 'asc' : 'desc',
      );
    }

    return stats;
  };

  renderDeleteMemberIcon = (id) => (
    <DeleteMemberIconBtn customAction={() => this.props.handleDeleteSlave(id)} params={id} />
  );

  renderStats = () => {
    const stats = this.splitStats(this.getOrderedStats());
    const { page, isMembers } = this.props;
    if (!stats[page]) {
      return null;
    }

    return stats[page].map((stat, key) => {
      const rowStats = this.normalizeData(stat, false);
      if (isMembers && this.props.tab === 'webmasters') {
        rowStats.push(this.renderDeleteMemberIcon(stat[1][0]));
      }
      const status =
        typeof rowStats[0] === 'object' && rowStats[0].props.children[1] ? 'disabled' : 'common';
      return <StatisticsTableRow tab={this.props.tab} key={key} stats={rowStats} status={status} />;
    });
  };

  renderTotal = () => {
    const { stats } = this.state;
    const { isMembers } = this.props;
    if (!stats.length) {
      return null;
    }
    const total = this.normalizeData(this.props.total, true);
    if (isMembers) {
      total.push('');
    }
    const firstCell = total[0] !== undefined ? <FMessage id="statistics.table.total" /> : null;
    return <StatisticsTableRow tab={this.props.tab} replaceFirstWith={firstCell} stats={total} />;
  };

  splitStats = (stats = []) => {
    const chunked = [];
    const { limit, setPagesCount, isPagination } = this.props;

    if (!isPagination) {
      setPagesCount(1);
      return [stats];
    }

    for (let i = 0, len = stats.length; i < len; i += limit) {
      chunked.push(stats.slice(i, i + limit));
    }

    setPagesCount(chunked.length);
    return chunked;
  };

  renderEmptyData = () => {
    const { config, loading, isMounted } = this.props;
    const { hiddenColumns } = this.state;

    const visibleColumnsCount = config.filter((cfg) => !hiddenColumns.includes(cfg.id)).length;

    if (visibleColumnsCount > 0) {
      return (
        <tbody>
          <tr>
            <td className="empty-stats" colSpan={visibleColumnsCount}>
              {loading ? (
                <FMessage id="statistics.table.loading" />
              ) : !isMounted ? null : (
                <FMessage id="statistics.table.no-data" />
              )}
            </td>
          </tr>
        </tbody>
      );
    }
    return null;
  };

  renderColumnControls = () => {
    const { config } = this.state;
    const {
      customStrings = {},
      hasTableEditingModalOpen,
      hasActionModalOpen,
      handleHasActionModalToggle,
      isMember,
      isSharing,
      setSharingState,
      showAlert,
      requestParams,
      theme,
      onRefresh,
      handleHasEditingModalToggle,
      handleHasExportModalToggle,
      master,
    } = this.props;
    const { group: customTitles } = customStrings;
    const allGroups = this.getAllGroups();
    const { isAddMemberModalOpen } = this.state;

    const tabProps = {
      master: master,
    };

    return (
      <>
        <EditingModal
          config={config}
          customTitles={customTitles}
          hasTableEditingModalOpen={hasTableEditingModalOpen}
          allGroups={allGroups}
          isPendingColumnHidden={this.isPendingColumnHidden}
          togglePendingColumnVisibility={this.togglePendingColumnVisibility}
          handleCancelChanges={this.handleCancelChanges}
          applyColumnChanges={this.applyColumnChanges}
        />

        <ActionModal
          isMasterAccPage={
            window.location.pathname === '/tools/master' || '/tools/master/:id([0-9]+)'
          }
          isOpen={hasActionModalOpen}
          isMember={isMember}
          isSharing={isSharing}
          setSharingState={setSharingState}
          showAlert={showAlert}
          requestParams={requestParams}
          theme={theme}
          handleRefresh={onRefresh}
          handleHasEditingModalToggle={handleHasEditingModalToggle}
          handleHasExportModalToggle={handleHasExportModalToggle}
          handleClose={handleHasActionModalToggle}
          handleOpenAddMemberModal={this.handleToggleAddMemberModal}
        />

        <AddMemberModal
          isOpen={isAddMemberModalOpen}
          handleClose={this.handleToggleAddMemberModal}
          tabProps={tabProps}
        />
      </>
    );
  };

  render() {
    const { stats } = this.state;
    const { tab, isLoadingScreenshot, theme, loading } = this.props;

    return (
      <>
        <StickyTable disabled={isLoadingScreenshot} theme={theme}>
          <Table
            tag={WithHover}
            className={`statistics-table sticky-table inverse statistics-table--${tab}`}
            responsive
            striped
            hover
            id="statistics-table"
          >
            <thead>
              <tr>{this.renderTableGroups()}</tr>
              <tr className="table-fields">{this.renderTableFields()}</tr>
            </thead>

            {stats.length > 0 && !loading ? (
              <Fragment>
                <tbody>{this.renderStats()}</tbody>
                <tfoot>{this.renderTotal()}</tfoot>
              </Fragment>
            ) : (
              this.renderEmptyData()
            )}
          </Table>
        </StickyTable>
        {this.renderColumnControls()}
      </>
    );
  }
}

const mapStateToProps = ({ app, webmaster, statistics, streams, slaves }) => {
  const {
    parsedQuery: { timeZone: tz },
  } = getUtlParams();
  const timeZone = tz
    ? tz
    : statistics.filters.timeZone
    ? getStringOrSingleArrayValue(statistics.filters.timeZone)
    : webmaster.profile.timezone;
  const master = isMaster();
  return {
    countries: app.globalSettings.countries,
    timeZones: app.globalSettings.timezones,
    sites: !master ? webmaster.sites : slaves.slaveSites,
    dict: !master ? statistics.dict : slaves.dict,
    filters: { ...statistics.filters, timeZone },
    offers: !master ? webmaster.offers : slaves.offers,
    flows: !master ? streams.streams : slaves.streams.streams,
    slaves: slaves.ids,
    currencies: app.globalSettings.currencies,
    theme: webmaster.profile.theme,
    master: webmaster.profile.master,
  };
};

export default connect(mapStateToProps)(StatisticsTable);
